import { defineStore } from 'pinia';
import { createActor } from 'xstate';
import { workflowMachine } from '@/stores/workflowMachine.js';
import {
    STATE_DISPLAY_NAMES,
    statesWithoutHeadline,
    QUESTION_STEPS_ORDER, QUESTION_STEPS_ORDER_FOR_MESSAGE_STATES,
} from '@/data/stateData.js';
import { sendTransitionTrackingData, sendPlzTrackingData } from '@/utils/dashboardTracking.js';
import { validZipCodesWP}  from "@/data/validZipCodesWp";
import { uuid } from 'vue-uuid';
import {sendTrackingPostMessage} from "@/utils/workflowMachineActions";

const getInitialPiniaState = () => ({
    workflowActor: null,
    currentMachineState: 'buildingTypeQuestion',
    workflowContext: { // Dies wird durch den XState-Snapshot aktualisiert
        appSessionId: uuid.v4(), // Generiere eine neue UUID beim Initialisieren
        fromState: "Geladen",
        toState: "Geladen",
        qualifikation: {
            alreadyBuilt: { apiDefinition: null, apiValue: null, },
            buildingType: { apiDefinition: null, apiValue: null, },
            owner: { apiDefinition: null, apiValue: null, },
            //buildingAge: { apiDefinition: null, apiValue: null, },
            planned_replacement: { apiDefinition: null, apiValue: null, },
            monument: { apiDefinition: null, apiValue: null, },
            //renovation: { apiDefinition: null, apiValue: null, },
            number_persons: { apiDefinition: null, apiValue: null, },
            heating_location: { apiDefinition: null, apiValue: null, },
            wishPV: {apiDefinition: null, apiValue: null,},
        },
        formData: {
            building: {
                zip: null, city: null, street: null, houseNumber: null,
                type: null, owner: false, year: null, planned_replacement: null, /*age: null,*/
                already_built: false,
                monument: null, renovation: null,  number_persons: null, heating_location: null,
                wish_pv: null,
                appointment: null,

            },
            contactForm: null,
            contactFormApiData: null,
            submissionResultData: null,
        },
        transitionCount: 0,
        errorMessage: null,
        crossdomainId: '',
        levetoSource: '',
        levetoMedium: '',
        levetoChannel: 'ON',
        apiFieldErrors: null,
        vertriebskanal: '',
    },
    isLoading: false,
    _subscription: null,
});

export const useWorkflowStore = defineStore('workflow', {
    state: getInitialPiniaState,

    getters: {
        isZipCodeQuestion: (state) => state.currentMachineState === 'zipCodeQuestion',
        isZipCodeNotAvailable: (state) => state.currentMachineState === 'zipCodeNotAvailable',
        isBuildingTypeQuestion: (state) => state.currentMachineState === 'buildingTypeQuestion',
        isBuildingTypeNotAvailable: (state) => state.currentMachineState === 'buildingTypeNotAvailable',
        isBuildingAgeQuestion: (state) => state.currentMachineState === 'buildingAgeQuestion',
        isBuildingAgeNotAvailable: (state) => state.currentMachineState === 'buildingAgeNotAvailable',
        isAlreadyBuiltQuestion: (state) => state.currentMachineState === 'alreadyBuiltQuestion',
        isAlreadyBuiltNotAvailable: (state) => state.currentMachineState === 'buildingYearNotAvailable',
        isMonumentQuestion: (state) => state.currentMachineState === 'monumentQuestion',
        isMonumentNotAvailable: (state) => state.currentMachineState === 'monumentNotAvailable',
        isRenovationQuestion: (state) => state.currentMachineState === 'renovationQuestion',
        isRenovationNotAvailable: (state) => state.currentMachineState === 'renovationNotAvailable',
        isOwnerQuestion: (state) => state.currentMachineState === 'ownerQuestion',
        isOwnerNotAvailable: (state) => state.currentMachineState === 'ownerNotAvailable',
        isPlannedReplacementQuestion: (state) => state.currentMachineState === 'plannedReplacementQuestion',
        isNumberPersonsQuestion: (state) => state.currentMachineState === 'numberPersonsQuestion',
        isHeatingLocationQuestion: (state) => state.currentMachineState === 'heatingLocationQuestion',
        isHeatingLocationNotAvailable: (state) => state.currentMachineState === 'heatingLocationNotAvailable',
        isWishPVQuestion: (state) => state.currentMachineState === 'wishPVQuestion',
        isAppointmentTypeQuestion: (state) => state.currentMachineState === 'appointmentTypeQuestion',
        isContactInformationQuestion: (state) => state.currentMachineState === 'contactInformationQuestion',
        isSendingVlinkData: (state) => state.currentMachineState === 'sendingVlinkData',
        isSubmissionError: (state) => state.currentMachineState === 'submissionError',
        isSubmitting: (state) => state.currentMachineState === 'submitting',
        getFormData: (state) => state.workflowContext.formData,
        getErrorMessage: (state) => state.workflowContext.errorMessage,
        shouldShowHeadline: (state) => {return !statesWithoutHeadline.includes(state.currentMachineState);},
        totalQuestionSteps: () => QUESTION_STEPS_ORDER.length,
        currentQuestionStep: (state) => {
            const index = QUESTION_STEPS_ORDER.indexOf(state.currentMachineState);
            if (index !== -1) {
                return index + 1;
            }
            return 0;
        },
        totalSteps: () => QUESTION_STEPS_ORDER_FOR_MESSAGE_STATES.length,
        currentStep: (state) => {
            const index = QUESTION_STEPS_ORDER_FOR_MESSAGE_STATES.indexOf(state.currentMachineState);
            if (index !== -1) {
                return index + 1;
            }
            return 0;
        },
        isSubmissionSuccess: (state) => state.currentMachineState === 'submissionSuccess',
        shouldShowBackButton(state) {
            const currentState = state.currentMachineState;
            const stepIndex = QUESTION_STEPS_ORDER.indexOf(currentState);
            return stepIndex > 0;
        },
    },

    actions: {
        initializeWorkflow() {
            if (!this.workflowActor) {
                this.workflowActor = createActor(workflowMachine);
                this.prevMachineStateForPostMessage = null;
                this._subscription = this.workflowActor.subscribe((snapshot) => {
                    this.currentMachineState = snapshot.value;
                    this.workflowContext = snapshot.context;
                    this.errorMessage = snapshot.context.errorMessage;
                    this.apiFieldErrors = snapshot.context.apiFieldErrors;
                    this.isLoading =  snapshot.matches('sendingVlinkData');
                    const newMachineState = snapshot.value; // Der NEUE Zustand nach der Transition
                    if (newMachineState && newMachineState !== this.prevMachineStateForPostMessage) {
                        sendTrackingPostMessage(newMachineState, snapshot.context);
                        this.prevMachineStateForPostMessage = newMachineState;
                        if (process.env.NODE_ENV !== 'production') {
                            console.log(newMachineState);
                        }
                    }
                });
                this.workflowActor.start();

                // Initialen Zustand und Kontext sofort synchronisieren
                const initialSnapshot = this.workflowActor.getSnapshot();
                this.currentMachineState = initialSnapshot.value;
                this.workflowContext = initialSnapshot.context;
                this.errorMessage = initialSnapshot.context.errorMessage;
                this.apiFieldErrors = initialSnapshot.context.apiFieldErrors;
                this.isLoading =  initialSnapshot.matches('sendingVlinkData');
                const initialDisplayState = STATE_DISPLAY_NAMES[initialSnapshot.value] || initialSnapshot.value;
                sendTransitionTrackingData(
                    'transition',
                    'Initial',
                    initialDisplayState,
                    'Initial',
                    initialSnapshot.value,
                    0,
                    this.workflowContext.appSessionId,
                );
            }
        },

        sendWorkflowEvent(event) {
            if (this.workflowActor) {
                const previousMachineState = this.workflowActor.getSnapshot().value;
                const previousDisplayState = STATE_DISPLAY_NAMES[previousMachineState] || previousMachineState;

                this.workflowActor.send(event); // Event an XState senden

                const newMachineState = this.workflowActor.getSnapshot().value;
                const newDisplayState = STATE_DISPLAY_NAMES[newMachineState] || newMachineState; // Sicherstellen, dass ein Display-Name vorhanden ist

                if (event.type === 'RESET_WORKFLOW') {
                    this.$reset(); // Setzt den Pinia-Store auf den initialen Zustand zurück
                    if (this.workflowActor) {
                        this.workflowActor.stop(); // Alten Actor stoppen
                        this.workflowActor = null; // Referenz löschen
                    }
                    this.initializeWorkflow(); // Workflow neu initialisieren
                    return;
                }

                // Transition Count und Tracking für NEXT/BACK Events
                if (event.type === 'NEXT' || event.type === 'BACK') {
                    if (this.workflowContext.transitionCount === undefined || this.workflowContext.transitionCount === null) {
                        this.workflowContext.transitionCount = 0;
                    }
                    this.workflowContext.transitionCount++;
                    sendTransitionTrackingData( // Funktion aus utils/tracking aufrufen
                        'transition',
                        previousDisplayState,
                        newDisplayState,
                        previousMachineState,
                        newMachineState,
                        this.workflowContext.transitionCount,
                        this.workflowContext.appSessionId,
                    );
                }

                // PLZ Tracking nur, wenn von zipCodeQuestion mit NEXT gewechselt wird
                if (event.type === 'NEXT' && previousMachineState === 'zipCodeQuestion'){
                    const status = this.workflowContext.formData.building.zip in validZipCodesWP ? 'gültig' : 'ungültig';
                    sendPlzTrackingData(this.workflowContext.appSessionId, this.workflowContext.formData.building.zip, status); // Funktion aus utils/tracking aufrufen
                }

                /*
                // Conversion Tracking wenn Formular erfolgreich abgesendet wurde
                if (event.type === 'VLINK_SUBMISSION_SUCCESS'){
                    sendTransitionTrackingData( // Funktion aus utils/tracking aufrufen
                        'conversion',
                        previousDisplayState,
                        'Abschluss: Formular abgesendet',
                        previousMachineState,
                        newMachineState,
                        this.workflowContext.transitionCount,
                        this.workflowContext.appSessionId,
                    );
                }
                */

                // fromState/toState im Kontext DES PINIA STORES nach dem Tracking aktualisieren
                this.workflowContext.fromState = previousDisplayState;
                this.workflowContext.toState = newDisplayState;
                this.workflowContext.fromMachineState = previousMachineState;
                this.workflowContext.toMachineState = newMachineState;
            } else {
                console.warn('Workflow-Actor nicht initialisiert. Bitte initializeWorkflow() aufrufen.');
            }
        },

        setCrossdomainId(id) {
            this.workflowContext.crossdomainId = id;
        },
        setVertriebskanal(vertriebskanal){
            this.workflowContext.vertriebskanal = vertriebskanal;
        },
        setLevetoSource(source) { // Neu
            this.workflowContext.levetoSource = source;
        },
        setLevetoMedium(medium) { // Neu
            this.workflowContext.levetoMedium = medium;
        },
        setLevetoChannel(channel) { // Neu
            this.workflowContext.levetoChannel = channel;
        },
        $dispose() {
            if (this._subscription) {
                this._subscription.unsubscribe();
                this._subscription = null;
            }
            if (this.workflowActor) {
                this.workflowActor.stop();
                this.workflowActor = null;
            }
        },
    },
});